import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EmailLog {
  id: string;
  user_id: string | null;
  to_email: string;
  template_key: string;
  subject: string | null;
  status: 'pending' | 'sent' | 'failed';
  resend_id: string | null;
  variables: Record<string, string>;
  error_message: string | null;
  created_at: string;
  sent_at: string | null;
  // Joined fields
  username?: string;
}

export interface ScheduledEmail {
  id: string;
  user_id: string | null;
  to_email: string;
  template_key: string;
  variables: Record<string, string>;
  scheduled_for: string;
  status: 'pending' | 'processing' | 'sent' | 'cancelled' | 'failed';
  error_message: string | null;
  created_at: string;
  processed_at: string | null;
  // Joined fields
  username?: string;
}

export interface EmailStats {
  total_sent: number;
  total_failed: number;
  total_pending: number;
  today_sent: number;
  scheduled_pending: number;
  by_template: Record<string, number>;
}

export function useEmailLogs(options?: {
  templateKey?: string;
  status?: string;
  limit?: number;
}) {
  const { templateKey, status, limit = 50 } = options || {};

  return useQuery({
    queryKey: ['email-logs', templateKey, status, limit],
    queryFn: async (): Promise<EmailLog[]> => {
      let query = supabase
        .from('email_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (templateKey) {
        query = query.eq('template_key', templateKey);
      }
      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch usernames for logs with user_id
      const userIds = [...new Set((data || []).filter(l => l.user_id).map(l => l.user_id))];
      
      let usernameMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', userIds);
        
        usernameMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p.username || 'Unknown';
          return acc;
        }, {} as Record<string, string>);
      }

      return (data || []).map(log => ({
        ...log,
        variables: log.variables as Record<string, string>,
        username: log.user_id ? usernameMap[log.user_id] : undefined,
      })) as EmailLog[];
    },
  });
}

export function useScheduledEmails() {
  return useQuery({
    queryKey: ['scheduled-emails'],
    queryFn: async (): Promise<ScheduledEmail[]> => {
      const { data, error } = await supabase
        .from('scheduled_emails')
        .select('*')
        .eq('status', 'pending')
        .order('scheduled_for', { ascending: false });

      if (error) throw error;

      // Fetch usernames
      const userIds = [...new Set((data || []).filter(e => e.user_id).map(e => e.user_id))];
      
      let usernameMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', userIds);
        
        usernameMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = p.username || 'Unknown';
          return acc;
        }, {} as Record<string, string>);
      }

      return (data || []).map(email => ({
        ...email,
        variables: email.variables as Record<string, string>,
        username: email.user_id ? usernameMap[email.user_id] : undefined,
      })) as ScheduledEmail[];
    },
  });
}

export function useEmailStats() {
  return useQuery({
    queryKey: ['email-stats'],
    queryFn: async (): Promise<EmailStats> => {
      const { data, error } = await supabase.rpc('get_email_stats');
      
      if (error) throw error;
      
      return data as EmailStats;
    },
  });
}

export function useCancelScheduledEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId: string) => {
      const { error } = await supabase
        .from('scheduled_emails')
        .update({ status: 'cancelled' })
        .eq('id', emailId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-emails'] });
      queryClient.invalidateQueries({ queryKey: ['email-stats'] });
    },
  });
}

export function useResendEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (log: EmailLog) => {
      const { data, error } = await supabase.functions.invoke('send-transactional-email', {
        body: {
          templateKey: log.template_key,
          toEmail: log.to_email,
          variables: log.variables,
          userId: log.user_id,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-logs'] });
      queryClient.invalidateQueries({ queryKey: ['email-stats'] });
    },
  });
}

export function useEmailTemplateByKey(templateKey: string | null) {
  return useQuery({
    queryKey: ['email-template', templateKey],
    queryFn: async () => {
      if (!templateKey) return null;
      
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_key', templateKey)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!templateKey,
  });
}
